import { useEffect, useState } from "react";
import { fetchCollections, CatalogCollection } from "@/lib/catalog";
import { VariantCard } from "./VariantCard";
import { Loader2 } from "lucide-react";
import { cn } from "@/lib/utils";

export const CollectionSections = () => {
  const [collections, setCollections] = useState<CatalogCollection[]>([]);
  const [loading, setLoading] = useState(true);
  const [activeIdx, setActiveIdx] = useState(0);

  useEffect(() => {
    fetchCollections(10)
      .then(setCollections)
      .catch(console.error)
      .finally(() => setLoading(false));
  }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  if (collections.length === 0) return null;

  const active = collections[activeIdx];

  // Flatten products into variant cards. Hide quote-only / wholesale-only
  // variants (price = 0) so the homepage stays focused on retail sizes.
  const variantCards = active.products.edges.flatMap((product) =>
    product.node.variants.edges
      .map((v, variantIdx) => ({ v, variantIdx }))
      .filter(({ v }) => parseFloat(v.node.price.amount) > 0 && v.node.id !== "USPK-T-B02")
      .map(({ variantIdx }) => ({
        product,
        variantIdx,
        key: `${product.node.id}-${variantIdx}`,
      }))
  );


  return (
    <section id="products" className="container py-8 md:py-24 scroll-mt-20">
      <div className="text-center mb-6 md:mb-12">
        <p className="text-gold font-medium tracking-[0.2em] uppercase text-sm mb-3">
          Our Collections
        </p>
        <h2 className="font-display text-3xl md:text-4xl font-bold">
          Premium Kashmiri Products
        </h2>
      </div>

      {/* Tabs (only show when multiple collections exist) */}
      {collections.length > 1 && (
        <div className="flex justify-center gap-2 mb-10 flex-wrap">
          {collections.map((col, i) => (
            <button
              key={col.id}
              onClick={() => setActiveIdx(i)}
              className={cn(
                "px-5 py-2.5 rounded-full text-sm font-medium transition-all duration-200 border",
                i === activeIdx
                  ? "bg-primary text-primary-foreground border-primary shadow-md"
                  : "bg-transparent text-muted-foreground border-border hover:border-primary/50 hover:text-foreground"
              )}
            >
              {col.title}
            </button>
          ))}
        </div>
      )}

      {/* Active collection description */}
      {active.description && (
        <p className="text-center text-muted-foreground mb-10 max-w-2xl mx-auto">
          {active.description}
        </p>
      )}

      {/* Variant cards grid */}
      {variantCards.length > 0 ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-6 md:gap-8">
          {variantCards.slice(0, 6).map((vc, i) => (
            <div key={vc.key} style={{ animationDelay: `${i * 100}ms` }}>
              <VariantCard product={vc.product} variantIdx={vc.variantIdx} />
            </div>
          ))}
        </div>
      ) : (
        <p className="text-center text-muted-foreground">
          No products in this collection yet.
        </p>
      )}
    </section>
  );
};
